import { ArrowRight, CheckCircle2, Star, Phone, FileText, Clock, Shield, AlertCircle, ChevronDown, ClipboardCheck, FileCheck, Users } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Contact from '../components/Contact';

export default function Zollabwicklung() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToContact = () => {
    const element = document.getElementById('kontakt');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const benefits = [
    'Komplette Zollabwicklung aus einer Hand',
    'Alle Dokumente werden professionell erstellt',
    'Keine Wartezeiten an der Grenze',
    'Erfahrung mit über 2000 Zollabwicklungen pro Jahr',
    'Rechtssichere Deklaration',
    'Im Umzugspreis bereits enthalten',
  ];

  const documents = [
    {
      icon: FileText,
      title: 'Übersiedlungsgutliste',
      description: 'Detaillierte Auflistung aller Umzugsgüter mit Mengen und Werten für die Zollbehörden.',
    },
    {
      icon: ClipboardCheck,
      title: 'Zollanmeldung',
      description: 'Offizielle Anmeldung des Umzugsguts beim deutschen Zoll mit allen erforderlichen Angaben.',
    },
    {
      icon: FileCheck,
      title: 'Abmeldebescheinigung',
      description: 'Nachweis Ihrer Abmeldung aus der Schweiz – oft erforderlich für zollfreie Einfuhr.',
    },
    {
      icon: Users,
      title: 'Vollmacht',
      description: 'Bevollmächtigung unseres Unternehmens zur Zollabwicklung in Ihrem Namen.',
    },
  ];

  const whyChooseUs = [
    {
      icon: Shield,
      title: 'Erfahrene Zollexperten',
      description: 'Unser Team kennt alle Schweizer und deutschen Zollbestimmungen im Detail. Mit über 2000 Zollabwicklungen pro Jahr sind wir Ihr verlässlicher Partner.',
    },
    {
      icon: Clock,
      title: 'Schnelle Abwicklung',
      description: 'Durch optimierte Prozesse und gute Kontakte zu den Zollbehörden wickeln wir Ihre Verzollung in der Regel innerhalb von 1-2 Stunden ab – ohne Wartezeiten für Sie.',
    },
    {
      icon: FileCheck,
      title: 'Rechtssicherheit',
      description: 'Alle Dokumente werden rechtssicher erstellt und entsprechen den aktuellen Zollvorschriften. Sie sind vor Nachforderungen und Strafen geschützt.',
    },
    {
      icon: CheckCircle2,
      title: 'Rundum-Service',
      description: 'Von der Dokumentenerstellung bis zur finalen Zollfreigabe übernehmen wir alles. Sie müssen sich um nichts kümmern – wir regeln alles für Sie.',
    },
  ];

  const commonMistakes = [
    {
      mistake: 'Unvollständige Übersiedlungsgutliste',
      consequence: 'Verzögerungen und mögliche Nachzahlungen',
      solution: 'Wir erstellen eine vollständige, detaillierte Liste aller Gegenstände',
    },
    {
      mistake: 'Fehlende Abmeldebescheinigung',
      consequence: 'Zollgebühren auf das gesamte Umzugsgut',
      solution: 'Wir informieren Sie rechtzeitig über alle benötigten Dokumente',
    },
    {
      mistake: 'Falsche Wertangaben',
      consequence: 'Strafen und Nachforderungen vom Zoll',
      solution: 'Korrekte Bewertung nach aktuellen Zollrichtlinien',
    },
    {
      mistake: 'Verbotene Güter im Umzugsgut',
      consequence: 'Beschlagnahmung und hohe Strafen',
      solution: 'Umfassende Beratung zu erlaubten und verbotenen Gütern',
    },
  ];

  const testimonials = [
    {
      name: 'Thomas W.',
      location: 'Zürich → München',
      text: 'Die Zollabwicklung lief völlig problemlos. Ich musste mich um nichts kümmern und hatte keine Wartezeit. Super professionell!',
      rating: 5,
    },
    {
      name: 'Sarah L.',
      location: 'Basel → Stuttgart',
      text: 'Hatte etwas Angst vor der Zollabwicklung, aber das Team hat alles perfekt erledigt. Alle Dokumente wurden erstellt und ich hatte null Stress!',
      rating: 5,
    },
    {
      name: 'Familie Meier',
      location: 'Bern → Frankfurt',
      text: 'Trotz grossem Umzug mit vielen Möbeln ging alles reibungslos über die Grenze. Die Zollabwicklung war im Preis inbegriffen. Top!',
      rating: 5,
    },
  ];

  const processSteps = [
    {
      step: '1',
      title: 'Bestandsaufnahme',
      description: 'Bei der Besichtigung erfassen wir alle zu transportierenden Güter für die Übersiedlungsgutliste.',
    },
    {
      step: '2',
      title: 'Dokumentenvorbereitung',
      description: 'Wir erstellen alle erforderlichen Zolldokumente: Übersiedlungsgutliste, Zollanmeldung, Vollmacht.',
    },
    {
      step: '3',
      title: 'Ihre Unterschrift',
      description: 'Sie unterschreiben die vorbereiteten Dokumente und bevollmächtigen uns zur Zollabwicklung.',
    },
    {
      step: '4',
      title: 'Voranmeldung',
      description: 'Wir melden Ihren Umzug beim Zoll vor und übermitteln alle Dokumente elektronisch.',
    },
    {
      step: '5',
      title: 'Grenzübertritt',
      description: 'Unser Team wickelt die Verzollung direkt am Grenzübergang ab – ohne Verzögerung.',
    },
    {
      step: '6',
      title: 'Zollfreigabe',
      description: 'Nach erfolgreicher Verzollung transportieren wir Ihre Güter direkt zu Ihrer neuen Adresse.',
    },
  ];

  const faqs = [
    {
      question: 'Was kostet die Zollabwicklung bei einem Umzug von der Schweiz nach Deutschland?',
      answer: 'Die <strong>Zollabwicklung ist bei unserem <a href="/komplettumzug" class="text-slate-900 font-semibold hover:underline">Komplettumzug</a> und <a href="/firmenumzuege" class="text-slate-900 font-semibold hover:underline">Firmenumzug</a> bereits enthalten</strong> – es fallen keine zusätzlichen Kosten an. Wenn Sie alle erforderlichen Voraussetzungen erfüllen (Wohnsitzverlegung, Abmeldung aus der Schweiz), ist die Einfuhr Ihres Umzugsguts nach Deutschland <strong>zoll- und steuerfrei</strong>. Sie zahlen also weder Zollgebühren noch Einfuhrumsatzsteuer. Mehr zu unseren <a href="/preise" class="text-slate-900 font-semibold hover:underline">Umzug Preisen</a>.'
    },
    {
      question: 'Welche Dokumente brauche ich für die Zollabwicklung?',
      answer: 'Für eine zoll- und steuerfreie Einfuhr benötigen Sie:<br><br><strong>1. Abmeldebescheinigung</strong> aus der Schweiz (von Ihrer Gemeinde)<br><strong>2. Nachweis des neuen Wohnsitzes</strong> in Deutschland (Mietvertrag oder Kaufvertrag)<br><strong>3. Personalausweis oder Reisepass</strong><br><strong>4. Übersiedlungsgutliste</strong> (erstellen wir für Sie)<br><strong>5. Vollmacht</strong> für die Zollabwicklung (erstellen wir für Sie)<br><br>Wir informieren Sie rechtzeitig über alle benötigten Unterlagen und erstellen die Zolldokumente für Sie.'
    },
    {
      question: 'Muss ich selbst zum Zoll oder können Sie das übernehmen?',
      answer: 'Sie müssen <strong>nicht persönlich zum Zoll</strong>. Wir übernehmen die komplette Zollabwicklung für Sie. Sie bevollmächtigen uns einmalig (durch Unterschrift auf der Vollmacht) und unser erfahrenes Team erledigt die gesamte Verzollung am Grenzübergang. Sie müssen nicht anwesend sein und können sich auf andere Dinge konzentrieren.'
    },
    {
      question: 'Wie lange dauert die Zollabwicklung an der Grenze?',
      answer: 'Dank unserer Voranmeldung und professionellen Vorbereitung dauert die Zollabwicklung am Grenzübergang in der Regel nur <strong>30-90 Minuten</strong>. In Ausnahmefällen (z.B. bei Stichprobenkontrollen) kann es etwas länger dauern. Durch unsere guten Kontakte zu den Zollbehörden und über 2000 Zollabwicklungen pro Jahr bei <a href="/umzug-zuerich-deutschland" class="text-slate-900 font-semibold hover:underline">Umzügen Zürich Deutschland</a>, <a href="/umzug-basel-deutschland" class="text-slate-900 font-semibold hover:underline">Umzügen Basel Deutschland</a> und anderen Routen läuft der Prozess sehr effizient ab.'
    },
    {
      question: 'Was passiert, wenn die Zolldokumente Fehler enthalten?',
      answer: 'Das wird bei uns nicht passieren. Unser Team erstellt täglich Zolldokumente und kennt alle Anforderungen im Detail. Sollte dennoch eine Unklarheit auftreten, klären wir diese sofort mit den Zollbehörden. Sie sind durch unsere Erfahrung und Expertise vollständig abgesichert. Bei Fehlern, die auf uns zurückzuführen sind, übernehmen wir selbstverständlich die Verantwortung.'
    },
    {
      question: 'Muss ich Zollgebühren oder Steuern zahlen?',
      answer: 'Wenn Sie Ihren Wohnsitz von der Schweiz nach Deutschland verlegen und eine <strong>Abmeldebescheinigung</strong> vorweisen können, ist die Einfuhr Ihres Umzugsguts <strong>zoll- und steuerfrei</strong>. Das gilt für alle Möbel, Haushaltsgeräte und persönlichen Gegenstände, die Sie mindestens 6 Monate genutzt haben. Neue, unbenutzte Waren oder Gegenstände mit hohem Wert (z.B. Luxusuhren über CHF 15\'000) müssen eventuell verzollt werden.'
    },
    {
      question: 'Welche Gegenstände darf ich nicht von der Schweiz nach Deutschland einführen?',
      answer: '<strong>Verboten sind:</strong><br>• Waffen und Munition (ohne Genehmigung)<br>• Drogen und Betäubungsmittel<br>• Gefälschte Markenprodukte<br>• Geschützte Tier- und Pflanzenarten<br>• Bestimmte Lebensmittel (Fleisch, Milchprodukte)<br><br><strong>Eingeschränkt sind:</strong><br>• Alkohol über 1 Liter (Spirituosen)<br>• Tabakwaren über 200 Zigaretten<br>• Bargeld über €10.000 (meldepflichtig)<br><br>Wir beraten Sie gerne im Detail zu erlaubten und verbotenen Gütern.'
    },
    {
      question: 'Was ist eine Übersiedlungsgutliste und wie wird sie erstellt?',
      answer: 'Die <strong>Übersiedlungsgutliste</strong> ist eine detaillierte Aufstellung aller Gegenstände, die Sie von der Schweiz nach Deutschland transportieren. Sie enthält:<br><br>• Genaue Beschreibung jedes Gegenstands<br>• Anzahl/Menge<br>• Zeitwert (nicht Neuwert!)<br>• Gesamtwert<br><br>Wir erstellen diese Liste für Sie bei der Besichtigung. Sie müssen nichts selbst aufschreiben. Die Liste wird in deutscher Sprache erstellt und entspricht allen Zollanforderungen.'
    },
    {
      question: 'Kann ich auch ohne Abmeldebescheinigung umziehen?',
      answer: 'Ja, Sie können auch ohne Abmeldebescheinigung umziehen, <strong>müssen dann aber Zoll und Einfuhrumsatzsteuer zahlen</strong>. Die Einfuhrumsatzsteuer beträgt 19% auf den Zeitwert Ihrer Güter. Das kann sehr teuer werden. Deshalb empfehlen wir dringend, sich vor dem Umzug offiziell aus der Schweiz abzumelden und die Bescheinigung vorzulegen. Wir beraten Sie gerne zu den verschiedenen Optionen.'
    },
    {
      question: 'Was passiert bei einer Zollkontrolle?',
      answer: 'Zollkontrollen sind <strong>Stichproben</strong> und kommen bei ca. 5-10% aller Umzüge vor. Dabei überprüft der Zoll, ob die Übersiedlungsgutliste mit dem tatsächlichen Transportgut übereinstimmt. Dank unserer sorgfältigen Vorbereitung verlaufen diese Kontrollen problemlos. Unser Team ist bei der Kontrolle anwesend und beantwortet alle Fragen der Zollbeamten. Für Sie entstehen dadurch keine Mehrkosten oder Probleme.'
    }
  ];

  const importantInfo = [
    {
      title: 'Fristen beachten',
      text: 'Die Abmeldebescheinigung sollte nicht älter als 6 Monate sein. Planen Sie Ihren Umzug entsprechend.',
      icon: Clock,
    },
    {
      title: 'Vollständige Angaben',
      text: 'Alle Gegenstände müssen in der Übersiedlungsgutliste aufgeführt sein. Nachträgliche Ergänzungen sind schwierig.',
      icon: FileText,
    },
    {
      title: 'Nutzungsdauer',
      text: 'Umzugsgut sollte mindestens 6 Monate in Ihrem Besitz und Gebrauch gewesen sein für zollfreie Einfuhr.',
      icon: AlertCircle,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white py-20 min-h-[700px] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }} />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold mb-6 border border-white/20">
              Zollabwicklung Schweiz → Deutschland
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Professionelle Zollabwicklung für Ihren Umzug
            </h1>

            <p className="text-xl md:text-2xl text-slate-200 mb-8 leading-relaxed">
              Wir übernehmen die komplette Verzollung – stressfrei, schnell und rechtssicher
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <button
                onClick={scrollToContact}
                className="bg-white text-slate-900 px-8 py-4 rounded-lg font-semibold hover:bg-slate-100 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center space-x-2"
              >
                <span>Gratis Beratung erhalten</span>
                <ArrowRight className="w-5 h-5" />
              </button>
              <a
                href="tel:0800700100"
                className="bg-slate-700/50 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-700 transition-all duration-300 border border-white/20 flex items-center justify-center space-x-2"
              >
                <Phone className="w-5 h-5" />
                <span>0800 700 100</span>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              {benefits.slice(0, 3).map((benefit, index) => (
                <div key={index} className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20">
                  <CheckCircle2 className="w-6 h-6 text-green-400 mb-2 mx-auto" />
                  <p className="text-sm font-medium">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Was ist Zollabwicklung?
              </h2>
              <p className="text-xl text-slate-600 leading-relaxed">
                Bei einem Umzug von der Schweiz nach Deutschland müssen Ihre Möbel und persönlichen Gegenstände beim deutschen Zoll angemeldet werden. Die Zollabwicklung umfasst alle administrativen Schritte, um Ihr Umzugsgut rechtssicher und zollfrei über die Grenze zu bringen. Bei unserem <Link to="/komplettumzug" className="text-slate-900 font-semibold hover:underline">Komplettumzug Schweiz Deutschland</Link> ist dieser Service komplett im Umzugspreis enthalten.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-8 border-2 border-blue-200">
              <h3 className="text-2xl font-bold text-slate-900 mb-6 text-center">
                Wichtig zu wissen:
              </h3>
              <div className="grid md:grid-cols-3 gap-6">
                {importantInfo.map((info, index) => {
                  const Icon = info.icon;
                  return (
                    <div key={index} className="text-center">
                      <div className="bg-blue-600 w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h4 className="font-bold text-slate-900 mb-2">{info.title}</h4>
                      <p className="text-sm text-slate-700">{info.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Diese Dokumente erstellen wir für Sie
              </h2>
              <p className="text-xl text-slate-600">
                Professionelle Dokumentenerstellung für reibungslose Zollabwicklung
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {documents.map((doc, index) => {
                const Icon = doc.icon;
                return (
                  <div key={index} className="bg-white rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-300 transition-all duration-300">
                    <div className="bg-slate-100 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-slate-800" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{doc.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{doc.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Warum Zollabwicklung mit uns?
              </h2>
              <p className="text-xl text-slate-600">
                Expertise, Erfahrung und Rundum-Service
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whyChooseUs.map((item, index) => {
                const Icon = item.icon;
                return (
                  <div key={index} className="bg-slate-50 rounded-2xl p-8 border-2 border-slate-100 hover:border-slate-300 transition-all duration-300 hover:shadow-lg">
                    <div className="bg-slate-800 w-14 h-14 rounded-xl flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-3">{item.title}</h3>
                    <p className="text-slate-600 leading-relaxed">{item.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Häufige Fehler bei der Zollabwicklung
              </h2>
              <p className="text-xl text-slate-600">
                Diese Probleme vermeiden Sie mit uns
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {commonMistakes.map((item, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 border-2 border-red-100">
                  <div className="flex items-start space-x-3 mb-4">
                    <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-lg font-bold text-slate-900 mb-2">{item.mistake}</h3>
                      <p className="text-red-600 text-sm font-semibold mb-3">⚠ {item.consequence}</p>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-start space-x-2">
                      <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-slate-700"><strong>Unsere Lösung:</strong> {item.solution}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                So läuft die Zollabwicklung ab
              </h2>
              <p className="text-xl text-slate-600">
                6 Schritte zur erfolgreichen Verzollung
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {processSteps.map((step, index) => (
                <div key={index} className="bg-slate-50 rounded-2xl p-6 border-2 border-slate-100 hover:border-slate-800 transition-all duration-300">
                  <div className="bg-slate-900 text-white w-12 h-12 rounded-xl flex items-center justify-center font-bold text-xl mb-4">
                    {step.step}
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
                  <p className="text-slate-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Das sagen unsere Kunden
              </h2>
              <p className="text-xl text-slate-600">
                Über 2000 erfolgreiche Zollabwicklungen pro Jahr
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {testimonials.map((testimonial, index) => (
                <div key={index} className="bg-white rounded-2xl p-6 border-2 border-slate-100">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-700 mb-4 leading-relaxed">"{testimonial.text}"</p>
                  <div className="border-t border-slate-200 pt-4">
                    <div className="font-semibold text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.location}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
                Häufig gestellte Fragen
              </h2>
              <p className="text-xl text-slate-600">
                Alles was Sie über Zollabwicklung wissen müssen
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-slate-50 rounded-xl border-2 border-slate-100 overflow-hidden transition-all duration-300 hover:border-slate-300"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left"
                  >
                    <span className="font-semibold text-slate-900 pr-4">{faq.question}</span>
                    <ChevronDown
                      className={`w-5 h-5 text-slate-600 transition-transform duration-300 flex-shrink-0 ${
                        openFaq === index ? 'transform rotate-180' : ''
                      }`}
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-6 pb-5">
                      <p className="text-slate-600 leading-relaxed" dangerouslySetInnerHTML={{ __html: faq.answer }} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Contact />
    </div>
  );
}
